import { yupResolver } from '@hookform/resolvers/yup';
import { Box, FormControlLabel, Typography } from '@mui/material';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { FunctionComponent, useState, useEffect, useCallback } from 'react';
import { FormProvider, useForm, SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Button,
  ConfirmationMessageModal,
  TextFieldWrapper,
  SelectFieldWrapper,
  TextEditorFieldWrapper,
  FullScreenLoader,
  Modal,
  ModalAlert,
  ToggleSwitch,
  ScrollToTop,
  PrintLayout,
  AutocompleteOptions,
} from '@Components/index';
import CommonConstants from '@Constants/common.constants';
import {
  GlobalScriptResponse,
  ScriptAdd,
  ScriptResponse,
} from '@Models/scripts.models';
import {
  useAddGlobalScriptMutation,
  useEditGlobalScriptMutation,
  useGetGlobalScriptQuery,
} from '@Services/globalScripts.service';
import { useGetAllGroupsQuery } from '@Services/groups.service';
import {
  useGetScriptQuery,
  useAddScriptMutation,
  useEditScriptMutation,
} from '@Services/scripts.service';
import { useGetTagsQuery } from '@Services/tags.service';
import {
  BaseAlertProps,
  CommonApiError,
  ConfirmationActionTypes,
  DialerCrudTypes,
} from '@Typings/global.types';
import {
  SelectOptions,
  getConfirmationMessageNode,
} from '@Utils/common/common.utils';
import query from '@Utils/common/query.utils';
import useAlert from '@Utils/hooks/useAlert.hook';
import usePrint from '@Utils/hooks/usePrint.hook';

import { ScriptsModalInputs, ScriptsModalProps } from './ScriptsModal.types';
import ScriptsModalSchema from './ScriptsModalSchema';

const ScriptsModal: FunctionComponent<ScriptsModalProps> = (
  props: ScriptsModalProps
): JSX.Element => {
  /** HOOKS */
  const { t } = useTranslation();
  const { triggerAlert } = useAlert();
  const { componentRef, handlePrint } = usePrint();

  const {
    scriptId,
    setScriptId,
    crudType,
    setCrudType,
    unarchiveBtnOnClick,
    deleteBtnOnClick,
    isAdmin,
  } = props;

  /** STATES */
  const [defaultType, setDefaultType] = useState<boolean>(false);
  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });
  const [modalAlertState, setModalAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [confirmationActionType, setConfirmationActionType] =
    useState<ConfirmationActionTypes | null>(null);
  /** This state helps to prevent error message from showing up when user is editing default script,
   * even if user has not performed any changes
   * This happens because of previous error which was cached by RTK
   * This is a fix until RTK Mutation supports resetting cache data */
  const [showDefaultScriptEditError, setShowDefaultScriptEditError] =
    useState<boolean>(false);

  /** FORM METHODS */
  const methods = useForm<ScriptsModalInputs>({
    resolver: yupResolver(ScriptsModalSchema()),
    mode: 'onChange',
    shouldUnregister: true,
  });

  /** RTK QUERY HOOKS */
  const {
    data: scriptData,
    isLoading: scriptIsLoading,
    isFetching: scriptIsFetching,
    error: getScriptError,
  } = useGetScriptQuery(scriptId, {
    skip: isAdmin || !scriptId || !crudType,
  });

  const {
    data: globalScriptData,
    isLoading: globalScriptIsLoading,
    isFetching: globalScriptIsFetching,
    error: getGlobalScriptError,
  } = useGetGlobalScriptQuery(scriptId, {
    skip: !isAdmin || !scriptId || !crudType,
  });

  const {
    data: allGroups,
    isLoading: allGroupsIsLoading,
    isFetching: allGroupsIsFetching,
  } = useGetAllGroupsQuery();

  const [addScript, addScriptMethods] = useAddScriptMutation();
  const [editScript, editScriptMethods] = useEditScriptMutation();
  const [addGlobalScript, addGlobalScriptMethods] =
    useAddGlobalScriptMutation();
  const [editGlobalScript, editGlobalScriptMethods] =
    useEditGlobalScriptMutation();
  const { data: placeholderTags } = useGetTagsQuery();

  /** LOADING STATE */
  const loading =
    scriptIsLoading ||
    scriptIsFetching ||
    globalScriptIsLoading ||
    globalScriptIsFetching ||
    allGroupsIsLoading ||
    allGroupsIsFetching ||
    addScriptMethods.isLoading ||
    addGlobalScriptMethods.isLoading ||
    editScriptMethods.isLoading ||
    editGlobalScriptMethods.isLoading;

  /** GET REQUEST RESPONSE DATA */
  const data: ScriptResponse | GlobalScriptResponse | undefined =
    scriptData || globalScriptData;

  /** GET REQUEST RESPONSE ERROR */
  const scriptError = getScriptError || getGlobalScriptError;

  /** ReadOnly prop for form fields if CRUD Type is View or Archive View */
  const readOnly =
    crudType === DialerCrudTypes.View || crudType === DialerCrudTypes.Archive;

  /** Select options */
  const options = SelectOptions();

  const groupOptions: AutocompleteOptions[] =
    allGroups?.map((group) => ({
      label: group.name,
      value: group.id,
    })) || [];

  /** Returns Modal Header Text */
  const getHeaderText = (): string | undefined => {
    if (loading) {
      return '';
    }

    return crudType === DialerCrudTypes.Add
      ? t('scripts.New Script')
      : data?.name;
  };

  /**
   * Closes modal by setting `crudType` state to null
   *
   * Resets `scriptId` state
   *
   * Removes modalAlert after modal close by setting `openAlert` state to false
   *
   * Resets error for default data edit by setting `showDefaultScriptEditError` state to false
   *
   * Resets form state
   */
  const handleClose = useCallback(() => {
    setCrudType(null);
    setScriptId('');
    setModalAlertState({ openAlert: false });
    setShowDefaultScriptEditError(false);
    methods.unregister();
  }, [methods, setCrudType, setScriptId]);

  /** SUCCESS AND ERROR ALERT HANDLERS --> */
  useEffect(() => {
    // GET SCRIPT ERROR
    if (scriptError) {
      handleClose();
      triggerAlert(scriptError, setAlertState);
    }
  }, [scriptError, handleClose, triggerAlert]);

  useEffect(() => {
    // ADD SCRIPT ERROR
    if (addScriptMethods.error) {
      if ((addScriptMethods.error as FetchBaseQueryError).status === 400) {
        methods.setValue('name', '');
      }
      triggerAlert(addScriptMethods.error, setModalAlertState);
    }

    if (addGlobalScriptMethods.error) {
      triggerAlert(addGlobalScriptMethods.error, setModalAlertState);
    }

    // ADD SCRIPT SUCCESS
    if (addScriptMethods.isSuccess || addGlobalScriptMethods.isSuccess) {
      // Trigger Success Alert according to Response `alert` key
      switch (addScriptMethods.data?.alert) {
        case CommonConstants.GLOBAL_SUCCESS_CODES.AUTO_MARK_DEFAULT:
          triggerAlert(
            CommonConstants.GLOBAL_MESSAGES.AUTO_ADD_DEFAULT_SCRIPT,
            setAlertState
          );
          break;
        case CommonConstants.GLOBAL_SUCCESS_CODES.SWAP_DEFAULT_RESOURCE:
          triggerAlert(
            CommonConstants.GLOBAL_MESSAGES.DEFAULT_SCRIPT_UPDATED,
            setAlertState
          );
          break;
        default:
          triggerAlert(
            CommonConstants.GLOBAL_MESSAGES.ADD_SCRIPT,
            setAlertState
          );
      }

      handleClose();
    }
  }, [
    methods,
    addScriptMethods.data,
    addScriptMethods.isSuccess,
    addScriptMethods.error,
    addGlobalScriptMethods.isSuccess,
    addGlobalScriptMethods.error,
    handleClose,
    triggerAlert,
  ]);

  useEffect(() => {
    // EDIT SCRIPT ERROR
    if (editScriptMethods.error) {
      setShowDefaultScriptEditError(true);
      if (
        (
          (editScriptMethods.error as FetchBaseQueryError)
            .data as CommonApiError
        ).message !== CommonConstants.GLOBAL_ERROR_CODES.RESOURCE_IS_A_DEFAULT
      ) {
        triggerAlert(editScriptMethods.error, setModalAlertState);
      }
    }

    if (editGlobalScriptMethods.error) {
      triggerAlert(editGlobalScriptMethods.error, setModalAlertState);
    }

    // EDIT SCRIPT SUCCESS
    if (editScriptMethods.isSuccess || editGlobalScriptMethods.isSuccess) {
      // Trigger Success Alert according to Response `alert` key
      switch (editScriptMethods.data?.alert) {
        case CommonConstants.GLOBAL_SUCCESS_CODES.SWAP_DEFAULT_RESOURCE:
          triggerAlert(
            CommonConstants.GLOBAL_MESSAGES.DEFAULT_SCRIPT_UPDATED,
            setAlertState
          );
          break;
        default:
          triggerAlert(
            CommonConstants.GLOBAL_MESSAGES.EDIT_SCRIPT,
            setAlertState
          );
      }

      handleClose();
    }
  }, [
    editScriptMethods.data?.alert,
    editScriptMethods.isSuccess,
    editScriptMethods.error,
    editGlobalScriptMethods.isSuccess,
    editGlobalScriptMethods.error,
    handleClose,
    triggerAlert,
  ]);

  useEffect(() => {
    // Trigger Error Alert according to Response `error` message
    // For RESOURCE_IS_A_DEFAULT MESSAGE
    if (
      editScriptMethods.error &&
      showDefaultScriptEditError &&
      ((editScriptMethods.error as FetchBaseQueryError).data as CommonApiError)
        .message === CommonConstants.GLOBAL_ERROR_CODES.RESOURCE_IS_A_DEFAULT
    ) {
      triggerAlert(
        t(
          `alertMessages.${CommonConstants.GLOBAL_MESSAGES.SCRIPT_IS_A_DEFAULT}`,
          { listType: scriptData?.type }
        ),
        setModalAlertState,
        'error',
        true
      );
    }
  }, [
    showDefaultScriptEditError,
    scriptData?.type,
    editScriptMethods.error,
    triggerAlert,
    t,
  ]);
  /** <-- SUCCESS AND ERROR ALERT HANDLERS */

  /** FORM HANDLERS --> */
  /** Handles default script toggle state change */
  const handleToggleChange = (
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => setDefaultType(checked);

  /**
   * View CRUD - Resets Form Fields with the API DATA
   * Add CRUD - Empty the Form Fields
   */
  useEffect(() => {
    if (data && crudType !== DialerCrudTypes.Add) {
      setDefaultType(isAdmin ? false : (data as ScriptResponse).default);
      methods.reset({
        name: data.name,
        type: {
          label: data.type,
          value: data.type,
        },
        content: data.content,
        group: {
          label: (data as ScriptResponse)?.group?.name,
          value: (data as ScriptResponse)?.group?.id,
        },
      });
    } else if (crudType === DialerCrudTypes.Add) {
      setDefaultType(false);
      methods.reset({
        name: '',
        type: {
          label: undefined,
          value: undefined,
        },
        content: '',
        group: {
          label: undefined,
          value: undefined,
        },
      });
    }
  }, [methods, data, isAdmin, crudType]);

  /** Form Submit Handler (Save Button Click) */
  const onSubmit: SubmitHandler<ScriptsModalInputs> = (formData) => {
    const userIdParam = query.get(CommonConstants.QUERY_PARAMS.USERS.USER_ID);

    const newData: ScriptAdd = {
      name: formData.name,
      type: formData.type.value,
      displayType: formData.type.value,
      content: formData.content,
      groupId: formData?.group?.value,
      default: defaultType,
      ...(userIdParam ? { userId: `${userIdParam}` } : {}),
    };

    if (data && crudType === DialerCrudTypes.Edit) {
      return isAdmin
        ? editGlobalScript({ ...data, ...newData })
        : editScript({ ...data, ...newData });
    }
    if (crudType === DialerCrudTypes.Add) {
      return isAdmin ? addGlobalScript(newData) : addScript(newData);
    }
  };
  /** <-- FORM HANDLERS */

  /** Button Group Component
   *
   * Returns Actions Button according to the CRUD Type.
   */
  const getButtonGroupComponent = (): JSX.Element | null => {
    switch (crudType) {
      case DialerCrudTypes.Add:
        return (
          <>
            <Button
              variant="text"
              color="secondary"
              sx={{ ml: 'auto', mr: 1.6 }}
              onClick={handleClose}
            >
              {t('common.messages.Cancel')}
            </Button>
            <Button
              type={
                defaultType && methods.formState.isValid ? 'button' : 'submit'
              }
              variant="contained"
              color="primary"
              onClick={() => {
                if (defaultType && methods.formState.isValid) {
                  setIsConfirmationModalOpen(true);
                  setConfirmationActionType(ConfirmationActionTypes.Save);
                }
              }}
            >
              {t('common.messages.Save')}
            </Button>
          </>
        );
      case DialerCrudTypes.View:
        return (
          <>
            {!scriptData?.global && (
              <>
                <Button
                  variant="outlined"
                  color="error"
                  disabled={scriptData?.default || !methods.formState.isValid}
                  onClick={() => {
                    setIsConfirmationModalOpen(true);
                    setConfirmationActionType(ConfirmationActionTypes.Delete);
                  }}
                >
                  {t('common.messages.Delete')}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  sx={{ ml: 'auto', mr: 1.6 }}
                  disabled={!methods.formState.isValid}
                  onClick={() => setCrudType(DialerCrudTypes.Edit)}
                >
                  {t('common.messages.Edit')}
                </Button>
              </>
            )}
            <Button
              variant="outlined"
              color="secondary"
              sx={{ ml: scriptData?.global ? 'auto' : undefined }}
              disabled={!methods.formState.isValid}
              onClick={handlePrint}
            >
              {t('common.messages.Print')}
            </Button>
          </>
        );
      case DialerCrudTypes.Edit:
        return (
          <>
            <Button
              variant="outlined"
              color="error"
              disabled={scriptData?.default || !methods.formState.isValid}
              onClick={() => {
                setIsConfirmationModalOpen(true);
                setConfirmationActionType(ConfirmationActionTypes.Delete);
              }}
            >
              {t('common.messages.Delete')}
            </Button>
            <Button
              variant="text"
              color="secondary"
              sx={{ ml: 'auto', mr: 1.6 }}
              onClick={handleClose}
            >
              {t('common.messages.Cancel')}
            </Button>
            <Button
              type={
                defaultType && methods.formState.isValid ? 'button' : 'submit'
              }
              variant="contained"
              color="primary"
              disabled={!methods.formState.isValid}
              onClick={() => {
                if (defaultType && methods.formState.isValid) {
                  setIsConfirmationModalOpen(true);
                  setConfirmationActionType(ConfirmationActionTypes.Save);
                }
              }}
            >
              {t('common.messages.Save')}
            </Button>
          </>
        );
      case DialerCrudTypes.Archive:
        return (
          <>
            <Button
              variant="outlined"
              color="error"
              disabled={!methods.formState.isValid}
              onClick={() => {
                setIsConfirmationModalOpen(true);
                setConfirmationActionType(ConfirmationActionTypes.Delete);
              }}
            >
              {t('common.messages.Delete')}
            </Button>
            <Button
              variant="text"
              color="secondary"
              sx={{ ml: 'auto', mr: 1.6 }}
              onClick={handleClose}
            >
              {t('common.messages.Cancel')}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              disabled={!methods.formState.isValid}
              onClick={() => {
                setIsConfirmationModalOpen(true);
                setConfirmationActionType(ConfirmationActionTypes.Unarchive);
              }}
            >
              {t('common.messages.Unarchive')}
            </Button>
          </>
        );
      default:
        return null;
    }
  };

  /** Handles confirmation click of modal and performs action accordingly */
  const handleConfirmationModalClick = () => {
    setIsConfirmationModalOpen(false);
    if (
      confirmationActionType === ConfirmationActionTypes.Delete &&
      deleteBtnOnClick
    ) {
      handleClose();
      return deleteBtnOnClick();
    }
    if (
      confirmationActionType === ConfirmationActionTypes.Unarchive &&
      unarchiveBtnOnClick
    ) {
      handleClose();
      return unarchiveBtnOnClick();
    }
  };

  /** Handles confirmation click of modal when default value is checked */
  const handleDefaultValueConfirmationModalClick = () => {
    setIsConfirmationModalOpen(false);
    onSubmit(methods.getValues());
  };

  return (
    <>
      <ConfirmationMessageModal
        isModalOpen={isConfirmationModalOpen}
        setIsModalOpen={setIsConfirmationModalOpen}
        message={getConfirmationMessageNode(
          confirmationActionType,
          scriptData?.default,
          methods.getValues('type.label')
        )}
        onClick={
          defaultType &&
          (crudType === DialerCrudTypes.Add ||
            crudType === DialerCrudTypes.Edit)
            ? handleDefaultValueConfirmationModalClick
            : handleConfirmationModalClick
        }
      />
      <Modal
        headerText={getHeaderText()}
        open={Boolean(crudType)}
        onClose={handleClose}
        alertState={alertState}
      >
        <FormProvider {...methods}>
          <Box
            className="script-modal-form"
            component="form"
            role="form"
            onSubmit={methods.handleSubmit(onSubmit)}
            sx={{
              position: 'relative',
              maxHeight: '70vh',
              padding: '3rem 2.4rem 2.4rem',
              overflow: loading ? 'hidden' : 'auto',
            }}
          >
            <ScrollToTop
              className="script-modal-form"
              dependencies={[loading]}
            />
            <PrintLayout
              ref={componentRef}
              printData={{
                name: methods.getValues('name'),
                listType: methods.getValues('type')?.value,
                isDefault: defaultType,
                body: methods.getValues('content'),
              }}
            />
            {loading ? (
              <FullScreenLoader
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  backgroundColor: 'common.white',
                  zIndex: 101,
                }}
              />
            ) : (
              <ModalAlert alertState={modalAlertState} />
            )}
            {isAdmin && (
              <Box display="flex" mb={3.8}>
                <SelectFieldWrapper
                  name="group"
                  options={groupOptions}
                  multiple={false}
                  textFieldProps={{
                    label: `${t('common.table.Group')}`,
                    placeholder: t('common.table.Select Group'),
                    InputProps: {
                      readOnly,
                    },
                  }}
                  sx={{ minWidth: '24.8rem', maxWidth: '24.8rem', mr: 5 }}
                />
              </Box>
            )}
            <TextFieldWrapper
              name="name"
              variant="outlined"
              label={`${t('common.messages.Name')} *`}
              placeholder={t('common.messages.Script Name')}
              sx={{
                minWidth: '51.2rem',
                mt: modalAlertState.openAlert ? 7 : undefined,
              }}
              InputProps={{
                readOnly,
              }}
            />
            <Box display="flex" mt={3.8}>
              <SelectFieldWrapper
                name="type"
                options={options}
                multiple={false}
                textFieldProps={{
                  label: `${t('common.table.List Type')} *`,
                  placeholder: t('common.table.Select List Type'),
                  InputProps: {
                    readOnly,
                  },
                }}
                sx={{ minWidth: '24.8rem', maxWidth: '24.8rem', mr: 5 }}
              />
              {!isAdmin && (
                <Box>
                  <Typography variant="main">
                    {t('scripts.Default Script')}
                  </Typography>
                  <FormControlLabel
                    control={
                      <ToggleSwitch
                        checked={defaultType}
                        readOnly={readOnly}
                        onChange={readOnly ? () => {} : handleToggleChange}
                      />
                    }
                    label={
                      <Typography variant="main">
                        {t(
                          defaultType
                            ? 'common.messages.Yes'
                            : 'common.messages.No'
                        )}
                      </Typography>
                    }
                  />
                </Box>
              )}
            </Box>
            <Box mt={3.2} mb={2.4}>
              <TextEditorFieldWrapper
                name="content"
                placeholderOptions={placeholderTags || []}
                placeholder={`${t('scripts.Type your script')} *`}
                readOnly={readOnly}
              />
            </Box>
            <Box display="flex" alignItems="center">
              {getButtonGroupComponent()}
            </Box>
          </Box>
        </FormProvider>
      </Modal>
    </>
  );
};

export default ScriptsModal;
